/*
 * AutoChimp Shipping Protection - native replacement for the Simply Insurance app.
 * Reads config from <script data-ac-sp-config> (rendered by snippets/ac-shipping-protection.liquid).
 *
 * Rules
 *  - Fee = the variant whose storefront (GST-inc) price is nearest 5% of the goods subtotal
 *    (protection line excluded); never the $0.00 variant; capped by the top variant.
 *  - OPT-IN (Daniel's decision, 19 Sep 2026): the line is only ever in the cart when the customer
 *    has switched it on, i.e. cart.attributes.shipping_protection == 'opted_in'. Nothing is pre-selected.
 *    The old auto-written value 'accepted' no longer counts, so carts from before the change start off.
 *  - Toggle on   -> adds the line, sets attribute 'opted_in'.
 *  - Toggle off  -> removes the line, sets attribute 'declined'.
 *  - Cart changes elsewhere (qty, remove, add) -> fee re-tiered automatically.
 *  - Goods subtotal 0 -> protection removed.
 *  - Exactly ONE protection line, quantity 1. Extra protection lines or quantity > 1 are corrected.
 *  - This script only ever adds or removes the PROTECTION line. It never touches product lines.
 *
 * v18 (20 Sep 2026): the fee re-tier is now ONE /cart/update.js request keyed by variant id
 * (old tier -> 0, new tier -> 1), so the cart never holds two protection lines at once.
 * v17 added the new tier first and removed the old one in a second request, which left a
 * ~1 s window where a customer checking out would pay the fee twice.
 */
(function () {
  'use strict';
  if (window.ACShippingProtection) return;

  var cfgEl = document.querySelector('[data-ac-sp-config]');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var busy = false;      // true while we are changing the cart ourselves
  var dirty = false;     // a refresh was requested while busy; run again when done
  var pending = null;    // debounce timer
  var lastCart = null;   // most recent cart we have seen (for instant optimistic updates)
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var YES = 'opted_in';  // the only attribute value that puts the protection line in the cart

  // Our own calls go through origFetch so the hook below only sees the theme's calls.
  function api(path, body) {
    return origFetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function getCart() {
    return origFetch(root + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (c) { lastCart = c; return c; });
  }

  function formatMoney(cents) {
    var fmt = cfg.moneyFormat || '${{amount}}';
    var amount = (cents / 100);
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    var out;
    switch (key) {
      case 'amount_no_decimals': out = Math.round(amount).toLocaleString('en-AU'); break;
      case 'amount_with_comma_separator': out = amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'); break;
      default: out = amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  function analyse(cart) {
    var goods = 0, line = null, lines = [];
    cart.items.forEach(function (it) {
      if (it.handle === cfg.handle) { line = it; lines.push(it); } else { goods += it.final_line_price; }
    });
    // Nearest non-zero tier to 5% of goods (tier prices are GST-inclusive, not exact 50c steps)
    var target = goods * cfg.rate, fee = 0, best = Infinity;
    Object.keys(cfg.tiers).forEach(function (k) {
      var price = Number(k);
      if (!price) return;
      var diff = Math.abs(price - target);
      if (diff < best || (diff === best && price > fee)) { best = diff; fee = price; }
    });
    var variantId = cfg.tiers[String(fee)];
    return {
      goods: goods,
      fee: fee,
      variantId: variantId,
      line: line,
      lines: lines,
      declined: !(cart.attributes && cart.attributes.shipping_protection === YES)
    };
  }

  function render(cart, a) {
    document.querySelectorAll('[data-ac-sp]').forEach(function (w) {
      w.classList.toggle('ac-sp--empty', a.goods === 0);
      var t = w.querySelector('[data-ac-sp-toggle]');
      var choice = queuedChoice || activeChoice;
      var declined = choice ? choice !== YES : a.declined;
      if (t) t.checked = !declined && a.goods > 0;
      var p = w.querySelector('[data-ac-sp-price]');
      if (p) p.textContent = formatMoney(a.fee);
      var d = w.querySelector('[data-ac-sp-desc]');
      if (d) d.textContent = declined ? cfg.declinedDesc : cfg.acceptedDesc;
    });
    // Empire cart page + add-to-cart banner totals
    var total = formatMoney(cart.total_price);
    document.querySelectorAll('[data-cart-total], [data-atc-banner-cart-subtotal]').forEach(function (el) {
      el.textContent = total;
    });
    document.querySelectorAll('[data-cartitem]').forEach(function (row) {
      var id = row.getAttribute('data-cartitem-id');
      var isSp = Object.keys(cfg.tiers).some(function (k) { return String(cfg.tiers[k]) === id; });
      if (isSp) row.classList.add('cart-item--protection');
    });
  }

  function setBusy(on) {
    busy = on;
    document.querySelectorAll('[data-ac-sp]').forEach(function (w) {
      if (on) w.setAttribute('data-ac-sp-busy', ''); else w.removeAttribute('data-ac-sp-busy');
    });
  }

  function hasExactlyOne(cart, variantId) {
    var sp = cart.items.filter(function (it) { return it.handle === cfg.handle; });
    return sp.length === 1 && sp[0].variant_id === variantId && sp[0].quantity === 1;
  }

  // Bring the cart in line with the rules, in as few requests as possible, then re-render.
  // choice (optional): 'opted_in' | 'declined' - the customer's new answer, written with the same request.
  function reconcile(cart, choice) {
    var attrs = choice ? { shipping_protection: choice } : null;
    var declined = choice ? choice !== YES : !(cart.attributes && cart.attributes.shipping_protection === YES);
    var a = analyse(cart);
    var wantLine = !declined && a.goods > 0 && a.variantId;
    var ops = null;

    if (!wantLine && a.lines.length) {
      // Remove every protection line in one request.
      var rm = {};
      a.lines.forEach(function (l) { rm[l.key] = 0; });
      ops = api('cart/update.js', attrs ? { updates: rm, attributes: attrs } : { updates: rm });
    } else if (wantLine && !hasExactlyOne(cart, a.variantId)) {
      // Swap tiers ATOMICALLY: one request, keyed by variant id, old tier(s) -> 0 and new tier -> 1.
      // The cart never holds two protection lines, so the fee can never be charged twice.
      var set = {};
      a.lines.forEach(function (l) { if (l.variant_id !== a.variantId) set[l.variant_id] = 0; });
      set[a.variantId] = 1;
      ops = api('cart/update.js', { updates: set, attributes: { shipping_protection: YES } })
        .then(function (fresh) {
          if (fresh && fresh.items && hasExactlyOne(fresh, a.variantId)) return fresh;
          // Fallback: if the store did not add the new tier from update.js, add it now. The old
          // tier is already gone, so at worst there is a moment with NO fee, never a double fee.
          var now = (fresh && fresh.items) ? fresh : null;
          var present = now && now.items.some(function (it) { return it.variant_id === a.variantId; });
          if (present) return getCart();
          return api('cart/add.js', { items: [{ id: a.variantId, quantity: 1 }] }).then(getCart);
        });
    } else if (attrs) {
      ops = api('cart/update.js', { attributes: attrs });
    }
    var afterOps = ops ? ops.then(function (fresh) {
      return (!fresh || !fresh.items) ? getCart() : fresh;
    }) : Promise.resolve(cart);

    return afterOps.then(function (fresh) {
      if (fresh === cart && !ops) return cart; // nothing changed
      lastCart = fresh;
      // Show the right numbers immediately, then let the server re-render the cart section.
      render(fresh, analyse(fresh));
      return rerenderCartSection().then(function () { return fresh; });
    });
  }

  // 20 Sep 2026: sinkProtection() was REMOVED. It kept the protection line as the oldest cart line by
  // setting every product line to quantity 0 and adding them back. For about a second the real cart held
  // only the protection fee; a customer who opened the cart or clicked Checkout in that gap lost their
  // products (one order went through with Shipping Protection only). NEVER zero, remove or re-create a
  // customer's product lines from this script. The only line it may add or remove is the protection line.

  // Re-render the cart section from the server and let Empire re-initialise it.
  // Empire's sections manager listens for the Theme Editor events, so firing
  // shopify:section:unload / shopify:section:load on the wrapper rebinds the +/- buttons,
  // remove links and shipping calculator against the new DOM.
  function rerenderCartSection() {
    var sec = document.querySelector('[data-section-type="static-cart"]');
    if (!sec) return Promise.resolve();
    var id = sec.getAttribute('data-section-id');
    var wrap = sec.parentNode;
    return origFetch(root + 'cart?section_id=' + encodeURIComponent(id) + '&_=' + Date.now(), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('shopify-section-' + id) || doc.body.firstElementChild;
        if (!fresh) return;
        wrap.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true, detail: { sectionId: id } }));
        wrap.innerHTML = fresh.innerHTML;
        wrap.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: id } }));
      })
      .catch(function () {});
  }

  function refresh() {
    if (busy) { dirty = true; return Promise.resolve(); }
    setBusy(true);
    dirty = false;
    return getCart().then(function (cart) {
      project(cart);
      return reconcile(cart);
    }).then(function (cart) {
      render(cart, analyse(cart));
    }).catch(function (e) {
      if (window.console) console.warn('[shipping-protection]', e);
    }).then(finish);
  }

  function scheduleRefresh() {
    // Empire has just morphed the section with server totals that still include the old
    // fee line; repaint the projected numbers immediately, then reconcile for real.
    if (lastCart) project(lastCart);
    clearTimeout(pending);
    pending = setTimeout(refresh, 60);
  }

  // Customer toggles the switch. If we are mid-request the choice is queued and applied
  // as soon as the current work finishes, so a click is never silently dropped.
  var queuedChoice = null;   // clicked while busy, applied next
  var activeChoice = null;   // being written right now; wins over any server render in between
  function applyChoice(choice) {
    setBusy(true);
    activeChoice = choice;
    var start = lastCart ? Promise.resolve(lastCart) : getCart();
    return start
      .then(function (cart) {
        var guess = Object.assign({}, cart, { attributes: Object.assign({}, cart.attributes, { shipping_protection: choice }) });
        project(guess);
        return reconcile(cart, choice);
      })
      .then(function (cart) { render(cart, analyse(cart)); })
      .catch(function (e) { if (window.console) console.warn('[shipping-protection]', e); })
      .then(finish);
  }
  function finish() {
    setBusy(false);
    activeChoice = null;
    if (queuedChoice) {
      var c = queuedChoice; queuedChoice = null;
      return applyChoice(c);
    }
    if (dirty) return refresh();
  }
  document.addEventListener('change', function (ev) {
    var t = ev.target;
    if (!t || !t.hasAttribute || !t.hasAttribute('data-ac-sp-toggle')) return;
    var choice = t.checked ? YES : 'declined';
    // Wait if we are mid-request, or if Empire is between a cart change and its own re-render
    // (re-rendering under it would make it reload the page).
    if (busy || themePending) { queuedChoice = choice; if (lastCart) project(lastCart); return; }
    applyChoice(choice);
  });

  // Watch the theme's own cart traffic (fetch and XHR) and re-tier after it.
  // On the cart page Empire follows every change with GET /cart?section_id=... and
  // re-renders the section from it; we wait for THAT request rather than the change
  // itself, so we never touch the DOM while Empire is mid-update.
  var onCartPage = !!document.querySelector('[data-section-type="static-cart"]');
  var themePending = false;  // cart page: Empire has changed the cart and not yet re-rendered
  var themePendingTimer = null;
  function themeStarted() {
    if (!onCartPage) return;
    themePending = true;
    clearTimeout(themePendingTimer);
    themePendingTimer = setTimeout(function () {
      themePending = false;
      if (queuedChoice && !busy) { var c = queuedChoice; queuedChoice = null; applyChoice(c); }
    }, 5000);
  }
  function themeSettled() {
    themePending = false;
    clearTimeout(themePendingTimer);
    scheduleRefresh();
  }
  function isTrigger(url) {
    if (typeof url !== 'string') return false;
    if (onCartPage) return /\/cart\?.*section_id=/.test(url);
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
  }
  // A cart mutation response from the theme lets us paint the projected fee and totals
  // straight away, before the reconcile round-trip.
  function isMutation(url) {
    return typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
  }
  function project(cart) {
    if (!cart || !cart.items || typeof cart.item_count !== 'number') return;
    lastCart = cart;
    var a = analyse(cart);
    var choice = queuedChoice || activeChoice;
    var declined = choice ? choice !== YES : a.declined;
    var wantLine = !declined && a.goods > 0;
    var total = a.goods + (wantLine ? a.fee : 0);
    var shown = Object.assign({}, cart, { total_price: total });
    render(shown, a);
  }
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url);
    if (isMutation(url)) themeStarted();
    var p = origFetch.apply(this, arguments);
    if (isMutation(url)) {
      p.then(function (r) { r.clone().json().then(project, function () {}); }, function () {});
    }
    if (isTrigger(url)) p.then(themeSettled, function () {});
    return p;
  };
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isMutation(url)) {
      themeStarted();
      this.addEventListener('load', function () {
        try { project(JSON.parse(this.responseText)); } catch (e) {}
      });
    }
    if (isTrigger(url)) {
      this.addEventListener('loadend', themeSettled);
    }
    return origOpen.apply(this, arguments);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  window.ACShippingProtection = { refresh: refresh, analyse: analyse };
})();
